import React, { useState, useEffect } from 'react';
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import apiClient from '../lib/api-client';

interface Group {
  id: string;
  name: string;
  description?: string;
  group_type: 'admin' | 'developer' | 'customer';
  scope: 'global' | 'project';
  project_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count?: number;
}

interface GroupMember {
  id: string;
  user_id: string;
  role: 'leader' | 'member';
  joined_at: string;
  user_name: string;
  user_email: string;
}

interface CreateGroupData {
  name: string;
  description?: string;
  group_type: 'admin' | 'developer' | 'customer';
  scope: 'global' | 'project';
  project_id?: string;
}

const GroupsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<'member' | 'leader'>('member');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createData, setCreateData] = useState<CreateGroupData>({
    name: '',
    description: '',
    group_type: 'developer',
    scope: 'global',
  });

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchGroups();
    fetchAllUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/groups');
      setGroups(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId: string) => {
    try {
      const response = await apiClient.get(`/groups/${groupId}`);
      setSelectedGroup(response.data);
      setGroupMembers(response.data.members || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch group details');
    }
  };

  const createGroup = async () => {
    if (!createData.name) return;
    
    try {
      await apiClient.post('/groups', createData);
      setShowCreateForm(false);
      setCreateData({
        name: '',
        description: '',
        group_type: 'developer',
        scope: 'global',
      });
      fetchGroups();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create group');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await apiClient.get('/users');
      setAllUsers(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const removeMember = async (groupId: string, userId: string) => {
    if (!window.confirm('Remove this member from the group?')) return;
    try {
      await apiClient.delete(`/groups/${groupId}/members/${userId}`);
      fetchGroupDetails(groupId);
      fetchGroups();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to remove member');
    }
  };

  const toggleRole = async (groupId: string, userId: string, currentRole: string) => {
    const newRole = currentRole === 'leader' ? 'member' : 'leader';
    try {
      await apiClient.patch(`/groups/${groupId}/members/${userId}`, { role: newRole });
      fetchGroupDetails(groupId);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to change role');
    }
  };

  const addMember = async () => {
    if (!selectedGroup || !addUserId) return;
    try {
      await apiClient.post(`/groups/${selectedGroup.id}/members`, {
        user_id: addUserId,
        role: addRole,
      });
      setShowAddMember(false);
      setAddUserId('');
      setAddRole('member');
      fetchGroupDetails(selectedGroup.id);
      fetchGroups();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add member');
    }
  };

  const getGroupTypeBadge = (type: string) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      developer: 'bg-blue-100 text-blue-800',
      customer: 'bg-green-100 text-green-800',
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
          <p className="mt-1 text-sm text-gray-500">
            You need admin privileges to manage groups.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Groups Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user groups for ticket assignment and permissions.
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            New Group
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="mt-2 text-red-600 underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Groups List */}
        <div className="lg:col-span-2">
          <div className="overflow-hidden bg-white shadow sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {groups.map((group) => (
                <li
                  key={group.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => fetchGroupDetails(group.id)}
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <p className="truncate text-sm font-medium text-indigo-600">
                          {group.name}
                        </p>
                        <span
                          className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getGroupTypeBadge(
                            group.group_type
                          )}`}
                        >
                          {group.group_type}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {group.description && (
                      <p className="mt-2 text-sm text-gray-500">{group.description}</p>
                    )}
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <span className="capitalize">{group.scope}</span>
                      {group.scope === 'project' && group.project_id && (
                        <span className="ml-1">- Project {group.project_id}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Group Details */}
        <div>
          {selectedGroup ? (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {selectedGroup.name}
                </h3>
                {selectedGroup.description && (
                  <p className="mt-1 text-sm text-gray-500">{selectedGroup.description}</p>
                )}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900">Members ({groupMembers.length})</h4>
                  <ul className="mt-2 space-y-2">
                    {groupMembers.map((member) => (
                      <li key={member.id} className="flex items-center justify-between py-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{member.user_name}</p>
                          <p className="text-xs text-gray-500 truncate">{member.user_email}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => toggleRole(selectedGroup!.id, member.user_id, member.role)}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer hover:opacity-80 ${
                              member.role === 'leader'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                            title={`Click to make ${member.role === 'leader' ? 'member' : 'leader'}`}
                          >
                            {member.role}
                          </button>
                          <button
                            onClick={() => removeMember(selectedGroup!.id, member.user_id)}
                            className="text-red-400 hover:text-red-600 text-xs px-1"
                            title="Remove from group"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {/* Add Member */}
                  {!showAddMember ? (
                    <button
                      onClick={() => setShowAddMember(true)}
                      className="mt-3 w-full inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" /> Add Member
                    </button>
                  ) : (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                      <select
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                      >
                        <option value="">Select user...</option>
                        {allUsers
                          .filter((u: any) => !groupMembers.find(m => m.user_id === u.id))
                          .map((u: any) => (
                            <option key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({u.email})
                            </option>
                          ))}
                      </select>
                      <select
                        value={addRole}
                        onChange={(e) => setAddRole(e.target.value as 'member' | 'leader')}
                        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                      >
                        <option value="member">Member</option>
                        <option value="leader">Leader</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={addMember}
                          className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => { setShowAddMember(false); setAddUserId(''); }}
                          className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6 text-center">
                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No group selected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select a group from the list to view details.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Create New Group</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={createData.group_type}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      group_type: e.target.value as 'admin' | 'developer' | 'customer',
                    })
                  }
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="developer">Developer</option>
                  <option value="admin">Admin</option>
                  <option value="customer">Customer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Scope</label>
                <select
                  value={createData.scope}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      scope: e.target.value as 'global' | 'project',
                    })
                  }
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="global">Global</option>
                  <option value="project">Project</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createGroup}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
